import { BaseThirdPartyAdWidget } from '../BaseThirdPartyAdWidget';
import { WidgetType } from '../../types';
import {
    VengoAd,
    VengoBaseUrls,
    VengoWidgetConfiguration,
    VengoWidgetConfigurationDetails,
    VengoWidgetTimelineSlot,
    ZodVengoWidgetConfigurationDetails,
} from './types';
import AdQueue from '../AdQueue';
import { Log } from '../../../AuxillaryService/Log';
import { VengoApiHelper } from './VengoApiHelper';
import { VengoVideoHandler } from '../../../MediaController/Video';
import { createVengoVideoHandler } from '../../../MediaController';
import { ReachAPI } from '../../../ReachApi';
import { glom } from '../../../Signage/glom';
import { PlayerTokenError } from '../errors';
import { GetVengoCredentialsAPISuccessResponse } from '../../../ReachApi/vengoTypes';

export class VengoWidget extends BaseThirdPartyAdWidget {
    type: WidgetType.VENGO;
    declare config: VengoWidgetConfigurationDetails;

    protected readonly adQueue: AdQueue<VengoAd> = new AdQueue();

    /**
     * Boolean determining whether the user
     * intends for the widget to fetch from Vengo's
     * test(staging) api or production.
     */
    private isTest: boolean;

    /**
     * The base url for the Vengo ad serving api
     * this widget will send ad requests to.
     */
    private vengoApiBaseUrl: VengoBaseUrls;

    /**
     * Name of the player / ad_unit making the ad request, recorded in
     * Vengo's ad serving platform.
     */
    private vengoPlayerId: string | null;

    /**
     * Name of the company requesting an ad, set in Vengo's system.
     */
    private vengoOrganizationId: string | null;

    constructor(widgetConfiguration: VengoWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.type = WidgetType.VENGO;
        this.isTest = this.config.is_test;
        this.vengoApiBaseUrl = this.isTest ? VengoBaseUrls.STAGING : VengoBaseUrls.PRODUCTION;
        this.vengoPlayerId = null;
        this.vengoOrganizationId = null;
    }

    protected validateConfigurationDetails(
        vengoWidgetConfigurationDetails: VengoWidgetConfigurationDetails
    ): VengoWidgetConfigurationDetails {
        return ZodVengoWidgetConfigurationDetails.parse(vengoWidgetConfigurationDetails);
    }

    /**
     * Attempts to fetch Vengo api credentials
     * from the REACH player api and set this
     * class's corresponding  props with the values
     * returned.
     * @throws {@link PlayerTokenError} when token player token or UUID
     * isn't set.
     */
    async getAndSetCredentials(): Promise<void> {
        if (!glom.playerUuid && !glom.securityToken) {
            throw new PlayerTokenError('Player uuid or security token must be set');
        }

        const security = new ReachAPI.Security(
            glom.playerUuid || undefined,
            glom.securityToken || undefined
        );

        try {
            const credentials: GetVengoCredentialsAPISuccessResponse =
                await ReachAPI.vengo.getCredentials(security, this.id);

            this.vengoOrganizationId = credentials.data.credentials.vengo_organization_id;
            this.vengoPlayerId = credentials.data.credentials.vengo_player_id;
        } catch (error) {
            Log.Warning(
                `${this.name} : Failed to retrieve credentials ${String(error)}`,
                null,
                __fileline
            );
        }
    }

    /**
     * Fetches Vengo ads.
     */
    async getAds(): Promise<void> {
        if (!this.vengoPlayerId || !this.vengoOrganizationId) {
            await this.getAndSetCredentials();
        }

        if (!this.vengoPlayerId || !this.vengoOrganizationId) {
            Log.Error(
                `${this.name} cannot fetch ads, Organization id or player id not set.`,
                null,
                __fileline
            );
            return;
        }
        await VengoApiHelper.fetchAds(
            this.vengoOrganizationId,
            this.vengoPlayerId,
            this.adQueue,
            this.numberOfAds,
            this.vengoApiBaseUrl
        );
    }

    async afterFadeOut(currentSlot: VengoWidgetTimelineSlot): Promise<void> {
        const currentAd = currentSlot.params.ad;

        if (!this.vengoPlayerId || !this.vengoOrganizationId) {
            Log.Error(
                `${this.name} Cannot send proof of play for ad ${currentAd.adId} organization id or player id not set.`
            );
        } else if (!currentAd.isFallback && currentAd.playcount >= 1) {
            await VengoApiHelper.sendProofOfPlay(
                currentAd,
                this.vengoPlayerId,
                this.vengoOrganizationId
            );
        }

        await super.afterFadeOut(currentSlot);
    }

    getThirdPartyAdVendorVideoHandler(assetUrl: string): VengoVideoHandler {
        return createVengoVideoHandler({
            videoUrl: assetUrl,
            muted: this.muteAds,
        });
    }
}
